import { Stock, StocksRecord } from '@/types';
import { getApiBase } from '@/lib/apiBase';

// Strip historicalData before saving to MongoDB to reduce storage
function stripHistoricalData(stocks: StocksRecord): StocksRecord {
  const result: StocksRecord = {};
  for (const [id, stock] of Object.entries(stocks)) {
    const { historicalData: _, ...stockWithoutHistory } = stock;
    result[id] = stockWithoutHistory as Stock;
  }
  return result;
}

// Auth is handled via Clerk session cookies, no userId needed as parameter
export async function loadPortfolio(): Promise<StocksRecord | null> {
  try {
    const response = await fetch(`${getApiBase()}/api/portfolio`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No portfolio found
      }
      throw new Error('Failed to load portfolio');
    }

    const data = await response.json();
    // Return stocks from cloud - they won't have historicalData, that's ok
    // historicalData will be populated by client when needed
    return data.stocks || {};
  } catch (error) {
    console.error('Error loading portfolio', error);
    return null;
  }
}

export async function savePortfolio(userId: string, stocks: StocksRecord): Promise<void> {
  // Strip historicalData before sending to reduce payload size
  const stocksWithoutHistory = stripHistoricalData(stocks);

  const response = await fetch(`${getApiBase()}/api/portfolio`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ stocks: stocksWithoutHistory }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `Failed to save portfolio (${response.status})`);
  }
}

export async function migrateLocalPortfolio(userId: string, localStocks: StocksRecord): Promise<boolean> {
  try {
    // Strip historicalData before sending to reduce payload size
    const stocksWithoutHistory = stripHistoricalData(localStocks);

    const response = await fetch(`${getApiBase()}/api/portfolio/migrate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stocks: stocksWithoutHistory }),
    });

    if (!response.ok) {
      throw new Error('Failed to migrate portfolio');
    }

    return true;
  } catch (error) {
    console.error('Error migrating portfolio', error);
    return false;
  }
}

export function mergePortfolios(cloudStocks: StocksRecord, localStocks: StocksRecord): StocksRecord {
  // Cloud takes precedence for portfolio composition
  // But we need to preserve historicalData from localStorage since it's not in the cloud
  if (cloudStocks && Object.keys(cloudStocks).length > 0) {
    const result: StocksRecord = {};
    for (const [id, cloudStock] of Object.entries(cloudStocks)) {
      // Find matching local stock by symbol for historicalData
      const localStock = Object.values(localStocks).find(ls => ls.symbol === cloudStock.symbol);
      result[id] = {
        ...cloudStock,
        historicalData: localStock?.historicalData || [],
        isLoading: false,
      };
    }
    return result;
  }
  return localStocks;
}
